import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterOutlet, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService } from '../../core/auth/auth.service';

interface NavItem {
  label: string;
  path: string;
  icon: string; // inline SVG path data
}

interface NavGroup {
  title: string;
  items: NavItem[];
  /** When set, the group is only rendered for a full ADMIN (see credentialAdminGuard). */
  fullAdminOnly?: boolean;
}

@Component({
  selector: 'app-shell',
  imports: [RouterOutlet, RouterLink, RouterLinkActive],
  template: `
    <div class="flex min-h-screen bg-[#f5f5f5] font-sans">
      <!-- Sidebar -->
      <aside
        class="fixed inset-y-0 left-0 z-30 flex w-72 flex-col bg-brand text-white transition-transform lg:sticky lg:top-0 lg:h-screen lg:translate-x-0"
        [class.-translate-x-full]="!sidebarOpen()"
      >
        <div class="flex h-20 items-center gap-3 border-b border-white/10 px-5">
          <img
            src="assets/FINAL-LOGO-1024x1024.png"
            alt="MMTEES"
            class="h-14 w-14 rounded-full object-contain"
          />
          <span class="text-2xl font-bold tracking-wide">MMTEES</span>
          <span class="ml-1 rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase">Admin</span>
        </div>

        <nav class="flex-1 overflow-y-auto px-3 py-4">
          @for (group of visibleNav(); track group.title) {
            <div class="mb-5">
              <p class="mb-2 px-2 text-[10px] font-bold uppercase tracking-widest text-white/40">
                {{ group.title }}
              </p>
              @for (item of group.items; track item.path) {
                <a
                  [routerLink]="item.path"
                  routerLinkActive="bg-accent text-white"
                  [routerLinkActiveOptions]="{ exact: false }"
                  class="mb-0.5 flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path [attr.d]="item.icon" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  {{ item.label }}
                </a>
              }
            </div>
          }
        </nav>
        <div class="border-t border-white/10 px-5 py-3 text-[11px] text-white/40">
          v0.0.0 · dev
        </div>
      </aside>

      <!-- Backdrop (mobile) -->
      @if (sidebarOpen()) {
        <div class="fixed inset-0 z-20 bg-black/40 lg:hidden" (click)="sidebarOpen.set(false)"></div>
      }

      <!-- Main column -->
      <div class="flex min-w-0 flex-1 flex-col">
        <header class="sticky top-0 z-10 flex h-16 items-center gap-3 border-b border-gray-200 bg-white px-4 lg:px-6">
          <button
            class="rounded-md p-2 text-brand hover:bg-gray-100 lg:hidden"
            (click)="sidebarOpen.set(!sidebarOpen())"
            aria-label="Toggle menu"
          >
            <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M4 6h16M4 12h16M4 18h16" stroke-linecap="round" />
            </svg>
          </button>
          <div class="flex-1"></div>
          <span class="hidden rounded-full bg-card px-3 py-1 text-xs font-semibold text-accent sm:inline">
            dev environment
          </span>
          @if (authed()) {
            <div class="flex h-9 w-9 items-center justify-center rounded-full bg-brand text-sm font-bold text-white"
              [title]="userLabel()">
              {{ initial() }}
            </div>
            <button class="btn-ghost px-2 py-1" (click)="logout()" title="Sign out">Logout</button>
          } @else {
            <a routerLink="/login" class="btn-primary px-3 py-1.5">Log in</a>
          }
        </header>

        <main class="flex-1 p-4 lg:p-6">
          <router-outlet />
        </main>
      </div>
    </div>
  `,
})
export class Shell {
  private auth = inject(AuthService);
  private router = inject(Router);

  sidebarOpen = signal(false);

  authed = computed(() => this.auth.isAuthenticated());
  userLabel = computed(() => {
    const u = this.auth.user();
    return u?.name || u?.email || 'Admin';
  });
  initial = computed(() => this.userLabel().charAt(0).toUpperCase() || 'A');

  logout(): void {
    this.auth.logout();
    this.router.navigate(['/login']);
  }

  visibleNav = computed(() => this.nav.filter((g) => !g.fullAdminOnly || this.auth.isFullAdmin()));

  nav: NavGroup[] = [
    {
      title: 'Overview',
      items: [{ label: 'Dashboard', path: '/dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h12a1 1 0 001-1V10' }],
    },
    {
      title: 'Catalog',
      items: [
        { label: 'Products', path: '/products', icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4' },
        { label: 'Categories', path: '/categories', icon: 'M4 6h16M4 10h16M4 14h10M4 18h10' },
        { label: 'Collections', path: '/collections', icon: 'M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z' },
      ],
    },
    {
      title: 'Sales',
      items: [
        { label: 'Orders', path: '/orders', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2' },
        { label: 'Coupons', path: '/coupons', icon: 'M7 7h.01M7 3h5a2 2 0 011.4.6l7 7a2 2 0 010 2.8l-5 5a2 2 0 01-2.8 0l-7-7A2 2 0 013 11V6a3 3 0 013-3z' },
        { label: 'Gift Vouchers', path: '/gift-vouchers', icon: 'M20 12v8a1 1 0 01-1 1H5a1 1 0 01-1-1v-8M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z' },
        { label: 'Transactions', path: '/transactions', icon: 'M12 8c-1.7 0-3 .9-3 2s1.3 2 3 2 3 .9 3 2-1.3 2-3 2m0-8V6m0 12v-2' },
      ],
    },
    {
      title: 'Customers',
      items: [
        { label: 'Users', path: '/users', icon: 'M17 20h5v-2a4 4 0 00-3-3.9M9 20H4v-2a4 4 0 013-3.9m6-2a4 4 0 100-8 4 4 0 000 8z' },
        { label: 'Reviews', path: '/reviews', icon: 'M11 3l2.5 5 5.5.8-4 3.9.9 5.5L11 16l-4.9 2.6.9-5.5-4-3.9L9 8z' },
        { label: 'Addresses', path: '/addresses', icon: 'M12 11a3 3 0 100-6 3 3 0 000 6zM12 2a7 7 0 00-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 00-7-7z' },
        { label: 'Carts', path: '/carts', icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 4h12M9 21a1 1 0 100-2 1 1 0 000 2zM17 21a1 1 0 100-2 1 1 0 000 2z' },
        { label: 'Wishlists', path: '/wishlists', icon: 'M11 3l2.5 5 5.5.8-4 3.9.9 5.5L11 16l-4.9 2.6.9-5.5-4-3.9L9 8z' },
      ],
    },
    {
      title: 'Content',
      items: [{ label: 'Banners', path: '/banners', icon: 'M4 5h16v10H4zM4 19h16' }],
    },
    {
      title: 'System',
      // Credential management is ADMIN-only; don't advertise it to a SUB_ADMIN who would be bounced.
      fullAdminOnly: true,
      items: [{ label: 'Integrations', path: '/settings/providers', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z' }],
    },
  ];
}
